import { inventoryStandardList } from "@/apis/product";
import useDataList from "@/hooks/useDataList";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import SText from "@/styles/SText";
import { InventoryStandard, Product } from "@/types/product";
import { Button, Flex, Input, TableColumnsType } from "antd";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useProductDeprected } from "../Product/hooks/useProductDeprected";
import { AnyObject } from "antd/es/_util/type";
import { CommonListParams } from "@/types/common";
import { numFormmater } from "../Requirement/util";
import { hasPermission } from "@/utils/user";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { UserPermissionEnum } from "@/types/user";
import AddOrEdit from "./components/AddOrEdit";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";

interface InventoryAlertFilters {
  keyword: string;
}

const INITIAL_INVENTORY_ALERT_FILTERS: InventoryAlertFilters = {
  keyword: "",
};

const InventoryAlert: FC = () => {
  const { t } = useTranslation();
  const { curUser } = useSelector((state: RootState) => state.userReducer)
  const tableLayoutRef = useRef<TableLayoutRef>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { getDeprecatedLabel } = useProductDeprected();

  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<InventoryAlertFilters>(
    INITIAL_INVENTORY_ALERT_FILTERS,
  );
  const { keyword } = draftFilters;
  const [list, total, loading, { fetchList, addItem, updateItem }] = useDataList<InventoryStandard, [CommonListParams]>({ fetchApi: inventoryStandardList });
  const [openDetail, setOpenDetail] = useState<InventoryStandard | null>(null);
  const [openAdd, setOpenAdd] = useState<boolean>(false);

  useEffect(() => {
    fetch(0, INITIAL_INVENTORY_ALERT_FILTERS);
    return () => abortControllerRef.current?.abort()
  }, []);

  const fetch = (offset: number, filters: InventoryAlertFilters) => {
    offset === 0 && tableLayoutRef.current && tableLayoutRef.current.resetTablePage();
    abortControllerRef.current?.abort();
    const control = new AbortController();
    abortControllerRef.current = control
    fetchList({
      body: filters.keyword,
      pagination: {
        offset,
        take: tableLayoutRef.current?.pageSize || DEFAULT_PAGE_SIZE
      },
      controller: control
    })
  };

  const handleAddOrEdit = (data: InventoryStandard | null, type: "ADD" | "EDIT") => {
    if (type === "ADD") setOpenAdd(true)
    if (type === "EDIT") {
      setOpenDetail(data);
    }
  };

  const handleClose = () => {
    setOpenAdd(false);
    setOpenDetail(null);
  };

  const handleSuccess = (data: InventoryStandard, type: "ADD" | "EDIT") => {
    if (type === "ADD") addItem(data)
    if (type === "EDIT") updateItem(data)
    handleClose();
  }

  const handleSearch = () => {
    const nextFilters = commitFilters();
    fetch(0, nextFilters);
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_INVENTORY_ALERT_FILTERS);
    fetch(0, nextFilters);
  };

  const columns: TableColumnsType<InventoryStandard> = [
    {
      width: 100,
      title: <SText text={t("product.code")} />,
      dataIndex: "productCode",
      sorter: (a, b) => {
        const valA = a?.product?.code ?? "";
        const valB = b?.product?.code ?? "";
        return valA.localeCompare(valB);
      },
      fixed: "left",
      render: (_, { product }) => <SText style={{ width: '100%' }} text={product?.code} />
    },
    {
      width: 120,
      title: <SText text={t("product.name")} />,
      dataIndex: "productName",
      render: (_, { product }) => <SText style={{ width: '100%' }} text={product?.name} />
    },
    {
      width: 140,
      title: <SText text={t("order.brand")} />,
      dataIndex: "model",
      render: (_, { product }) => <SText style={{ width: '100%' }} text={product?.model} />
    },
    {
      width: 100,
      title: <SText text={t("product.uom")} />,
      dataIndex: "productUom",
      render: (_, { product }) => <SText text={product?.uom} />,
    },
    {
      width: 100,
      title: <SText text={`Process(${t('product.process')})`} />,
      dataIndex: "productProcess",
      render: (_, { product }) => <SText text={product?.process || ''} />,
    },
    {
      width: 100,
      dataIndex: "category",
      title: <SText text={t("product.category")} />,
      hidden: true,
      render: (_, { product }) => <SText text={product?.category?.name || ''} />,
    },
    {
      width: 100,
      dataIndex: "preReady",
      title: <SText text={t("product.preReady")} />,
      hidden: true,
      render: (_, { product }) => <SText text={product?.preReady === 1 ? t("product.isTrue") : t("product.isFalse")} />,
    },
    {
      width: 100,
      dataIndex: "deprecated",
      title: <SText text={t("product.isDeprecated")} />,
      fixed: "right",
      hidden: true,
      render: (_, { product }) => {
        const label = getDeprecatedLabel(product);
        return label && <SText tagMode text={label.text} color={label.color} />
      },
    },
    {
      width: 100,
      dataIndex: "minStockCount",
      title: <SText text={t("product.minStockCount")} />,
      fixed: "right",
      render: (_, record) => numFormmater(record.minStockCount)
    },
    {
      width: 100,
      dataIndex: "maxStockCount",
      title: <SText text={t("product.maxStockCount")} />,
      fixed: "right",
      render: (_, record) => numFormmater(record.maxStockCount)
    },
    {
      width: 100,
      dataIndex: "minStockAgeDays",
      title: <SText text={t("product.minStockAgeDays")} />,
      fixed: "right",
      render: (_, record) => numFormmater(record.minStockAgeDays)
    },
    {
      width: 100,
      dataIndex: "maxStockAgeDays",
      title: <SText text={t("product.maxStockAgeDays")} />,
      fixed: "right",
      render: (_, record) => numFormmater(record.maxStockAgeDays)
    },
    {
      width: 100,
      title: <SText text={t("common.opt")} />,
      dataIndex: "Action",
      fixed: "right",
      render: (_, record) => <Button type="link" onClick={() => handleAddOrEdit(record, "EDIT")} >{t('common.detail')}</Button>
    },
  ];

  const headerChildren = (
    <Flex justify="space-between" style={{ width: "100%", padding: "0 4px" }}>
      {hasPermission(curUser, [UserPermissionEnum.MATERIAL_INVENTORY_ALERT_SET]) && <Button type="primary" onClick={() => handleAddOrEdit(null, "ADD")} >{t('common.add')}</Button>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={loading}
        >
          {({ submit }) => (
            <Input
              value={keyword}
              onChange={event =>
                updateDraftFilters({ keyword: event.target.value })
              }
              onPressEnter={submit}
              placeholder={t('product.codePl')}
              style={{ width: 200 }}
            />
          )}
        </ListFilterBar>
      </div>
    </Flex>
  )

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <STableLayout
        dataSource={list}
        loading={loading}
        columns={columns as Array<AnyObject>}
        total={total}
        headerChildren={headerChildren}
        tableLayoutRef={tableLayoutRef}
        handleChange={offset => fetch(offset, appliedFilters)}
      />
      {openAdd && <AddOrEdit detail={undefined} formType="ADD" open={openAdd} onClose={handleClose} onSuccess={handleSuccess} />}
      {!!openDetail && <AddOrEdit detail={openDetail} formType="EDIT" open onClose={handleClose} onSuccess={handleSuccess} readonly={!hasPermission(curUser, [UserPermissionEnum.MATERIAL_INVENTORY_ALERT_SET])} />}
    </Flex>
  )
};

export default InventoryAlert;
